import { useEffect, useState } from "react";
import { PHASES } from "./lunelle-data";

export const CURRENCIES = [
  ["USD", "US dollar"], ["EUR", "Euro"], ["GBP", "British pound"], ["CAD", "Canadian dollar"], ["AUD", "Australian dollar"],
  ["NGN", "Nigerian naira"], ["GHS", "Ghanaian cedi"], ["KES", "Kenyan shilling"], ["ZAR", "South African rand"],
  ["INR", "Indian rupee"], ["AED", "UAE dirham"], ["JPY", "Japanese yen"], ["BRL", "Brazilian real"], ["MXN", "Mexican peso"],
];

export const FIELDS = [
  ["income", "Monthly income", "money"],
  ["bills", "Bills & rent", "money"],
  ["savings", "Savings goal", "money"],
  ["food", "Food & groceries", "money"],
  ["activities", "Activities & fun", "money"],
  ["periodCare", "Period products", "care"],
  ["selfCare", "Self-care & wellness", "care"],
];

const DEFAULTS = { currency: "USD", income: 3000, bills: 1400, savings: 300, food: 400, activities: 150, periodCare: 25, selfCare: 75 };
const KEY = "lunelle-budget";
const EVT = "lunelle-budget-change";

const WEIGHT = { menstrual: 0.9, follicular: 1, ovulatory: 1.1, luteal: 1.15 };
const FOOD_WEIGHT = { menstrual: 1.05, follicular: 0.95, ovulatory: 0.95, luteal: 1.1 };
const FUN_WEIGHT = { menstrual: 0.6, follicular: 1.15, ovulatory: 1.4, luteal: 0.9 };

const splitByPhase = (total, weights) => {
  const sum = PHASES.reduce((s, p) => s + (p.days[1] - p.days[0] + 1) * weights[p.id], 0);
  return Object.fromEntries(PHASES.map((p) => [p.id, Math.max(0, total) * (((p.days[1] - p.days[0] + 1) * weights[p.id]) / sum)]));
};

export const PHASE_TIPS = {
  menstrual: "Energy is low, so plan cosy nights in. Stock up on period care before day 1 to skip last-minute prices. Batch-cook warming meals to avoid takeaway.",
  follicular: "A clear-headed week: review subscriptions, set goals and try a new class or outing while energy rises.",
  ovulatory: "Social energy peaks, so your fun money gets the most per day now. Pick the plans you love most.",
  luteal: "Some studies link this phase with more impulse buying, and appetite often rises. Try a 24-hour pause before non-essentials and plan hearty, filling meals.",
};

const load = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(KEY) || "{}");
    return { ...DEFAULTS, ...(saved && typeof saved === "object" ? saved : {}) };
  } catch {
    return DEFAULTS;
  }
};

export const useBudget = () => {
  const [budget, setBudget] = useState(load);
  useEffect(() => {
    const s = JSON.stringify(budget);
    if (window.localStorage.getItem(KEY) === s) return;
    window.localStorage.setItem(KEY, s);
    window.dispatchEvent(new Event(EVT));
  }, [budget]);
  useEffect(() => {
    const sync = () => setBudget((prev) => {
      const next = load();
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, []);
  const set = (key, value) => setBudget((b) => ({ ...b, [key]: value }));
  const reset = () => setBudget(DEFAULTS);
  return { budget, set, reset };
};

const num = (v) => Math.max(0, Number(v) || 0);

export const computeBudget = (b) => {
  const income = num(b.income);
  const bills = num(b.bills);
  const savings = num(b.savings);
  const care = num(b.periodCare) + num(b.selfCare);
  const food = num(b.food);
  const activities = num(b.activities);
  const flexible = income - bills - savings - care - food - activities;
  const flex = splitByPhase(flexible, WEIGHT);
  const foodSplit = splitByPhase(food, FOOD_WEIGHT);
  const funSplit = splitByPhase(activities, FUN_WEIGHT);
  const phases = PHASES.map((p) => {
    const days = p.days[1] - p.days[0] + 1;
    const amount = flex[p.id];
    return { ...p, daysCount: days, amount, perDay: amount / days, food: foodSplit[p.id], activities: funSplit[p.id] };
  });
  return { income, bills, savings, care, food, activities, flexible, phases };
};

export const formatMoney = (value, currency) => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${Math.round(value)} ${currency}`;
  }
};
